import io from "socket.io-client";
import { isExpired } from "react-jwt";
import api from "./api";

class SocketWorker {
  token = null;
  lastEventAt = Date.now();
  healthInterval = null;
  
  constructor(companyId , userId) {
    if (
      !SocketWorker.instance
      || SocketWorker.instance.companyId !== companyId
      || SocketWorker.instance.userId !== userId
    ) {
      this.companyId = companyId
      this.userId = userId
      this.socket = null;
      this.configureSocket();
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      SocketWorker.instance = this;
    } 

    return SocketWorker.instance;
  }

  configureSocket() {
    this.token = JSON.parse(localStorage.getItem("token"));
    
    if (!this.token) {
      console.error("Token não encontrado");
      return;
    }
    
    // Usar REACT_APP_BACKEND_URL se REACT_APP_BACKEND_SOCKET não estiver definido
    const socketUrl = process.env.REACT_APP_BACKEND_SOCKET || process.env.REACT_APP_BACKEND_URL;
    this.socket = io(`${socketUrl}/${this?.companyId}` , {
      transports: ["websocket", "polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      forceNew: true,
      query: { token: this.token }
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
      // Entrar em todas as salas de conexões disponíveis para garantir reatividade total
      try {
        api.get("/whatsapp/?session=0").then(({ data }) => {
          if (Array.isArray(data)) {
            data.forEach((w) => {
              if (w?.id) {
                this.socket.emit("joinConnection", w.id);
              }
            });
          }
        }).catch((err) => {
          console.debug("Falha ao listar conexões para auto-join:", err?.message || err);
        });
      } catch (e) {
        console.debug("Erro ao tentar auto-join de conexões:", e?.message || e);
      }
      this.lastEventAt = Date.now();
    });

    this.socket.on("connect_error", async (error) => {
      try {
        if (isExpired(this.token)) {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            this.token = data.token;
            this.socket.io.opts.query.token = this.token;
          }
        }
      } catch (_) {}
      console.error("Erro de conexão Socket.IO:", error);
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO:", reason);
      this.reconnectAfterDelay();
    });

    this.socket.io.on("reconnect_attempt", async () => {
      try {
        if (isExpired(this.token)) {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            this.token = data.token;
            this.socket.io.opts.query.token = this.token;
          } else {
            window.location.href = "/login";
          }
        }
      } catch (_) {
        window.location.href = "/login";
      }
      this.socket.io.opts.query.r = 1;
    });
    if (this.socket && this.socket.onAny) {
      this.socket.onAny((event) => {
        if (typeof event === "string" && event.startsWith("company-")) {
          this.lastEventAt = Date.now();
        }
      });
    }
    if (!this.healthInterval) {
      this.healthInterval = setInterval(() => {
        const now = Date.now();
        const stale = now - this.lastEventAt > 60000;
        if (!this.socket || !this.socket.connected) {
          this.connect();
          return;
        }
        if (stale) {
          try {
            api.get("/whatsapp/?session=0").then(({ data }) => {
              if (Array.isArray(data)) {
                data.forEach((w) => {
                  if (w?.id) {
                    this.socket.emit("joinConnection", w.id);
                  }
                });
              }
              this.socket.emit("joinNotification");
            }).catch(() => {});
            this.lastEventAt = Date.now();
          } catch (_) {}
        }
      }, 30000);
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
          if (!document.hidden) {
            this.lastEventAt = Date.now();
            this.connect();
            try {
              api.get("/whatsapp/?session=0").then(({ data }) => {
                if (Array.isArray(data)) {
                  data.forEach((w) => {
                    if (w?.id) {
                      this.socket.emit("joinConnection", w.id);
                    }
                  });
                }
                this.socket.emit("joinNotification");
              }).catch(() => {});
            } catch (_) {}
          }
        });
      }
    }
      
    this.socket.on("disconnect", () => {
      console.log("Desconectado do servidor Socket.IO");
      this.reconnectAfterDelay();
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null
      this.instance = null
      this.token = null
      SocketWorker.instance = null
      console.log("Socket desconectado");
      if (this.healthInterval) {
        clearInterval(this.healthInterval);
        this.healthInterval = null;
      }
    }
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {

  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
